import { EcosystemEnum } from "@clockwise/schema";
import { CalendarDensity, CalendarMode } from "@clockwise/web-commons/src/components/calendar";
import { CalendarMenu } from "@clockwise/web-commons/src/components/calendar/calenar-menu/CalendarMenu";
import { CalendarMoreOptions } from "@clockwise/web-commons/src/components/calendar/calendar-more/CalendarMoreOptions";
import {
  CalendarStepNavigation,
  HighlightedNavButton,
} from "@clockwise/web-commons/src/components/calendar/calendar-step-navigation/CalendarStepNavigation";
import { Notification } from "@clockwise/web-commons/src/ui/Notification";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { DatePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import classNames from "classnames";
import { DateTime } from "luxon";
import React from "react";
import { CalendarIds } from "../../../state/actions/multi-calendar.actions";
import { changelogURL, feedbackURL } from "../../chat/constants";
import { MultiCalendarSelect } from "../multi-calendar-select/";

type Props = {
  calendarPeek?: boolean;
  showHolds?: boolean;
  compact: boolean;
  currentDate: string;
  declinedEventsEnabled: boolean;
  density: CalendarDensity;
  ecosystem: EcosystemEnum;
  errorMessage: string | undefined;
  loading: boolean;
  mode: CalendarMode;
  onCalendarDensityChange: (density: CalendarDensity) => void;
  onCalendarModeChange: (view: CalendarMode) => void;
  onMultiCalAdd: (calendarIds: string[]) => void;
  onMultiCalRemove: (calendarIds: string[]) => void;
  onShowHoldsChange: (collapse: boolean) => void;
  onNavigateToDate: (date: string) => void;
  onSchedule?: (roomRequired: boolean) => void;
  onShiftNext: () => void;
  onShiftPrevious: () => void;
  onShowDeclinedEventsChange: (shown: boolean) => void;
  onShowWeekendsChange: (shown: boolean) => void;
  onTodayClick: () => void;
  previewedCalendarId: CalendarIds;
  selectedCalendarIds: CalendarIds;
  weekendsEnabled: boolean;
  navButtonToHighlight: HighlightedNavButton;
  highlightedNavButtonTooltip: string | null;
};

export const PlannerToolbarRender = ({
  calendarPeek = false,
  compact,
  currentDate,
  declinedEventsEnabled,
  density,
  errorMessage,
  loading,
  mode,
  onCalendarDensityChange,
  onCalendarModeChange,
  onMultiCalAdd,
  onMultiCalRemove,
  onNavigateToDate,
  onShiftNext,
  onShiftPrevious,
  onShowDeclinedEventsChange,
  onShowHoldsChange,
  onShowWeekendsChange,
  onTodayClick,
  previewedCalendarId,
  selectedCalendarIds,
  showHolds = false,
  weekendsEnabled,
  navButtonToHighlight,
  highlightedNavButtonTooltip,
}: Props) => {
  const currentJSDate = DateTime.fromISO(currentDate, { zone: getRenderTimeZone() }).toJSDate();

  const handleNavigateToDate = (dateMoment: MaterialUiPickersDate) => {
    if (!dateMoment) {
      return;
    }
    return onNavigateToDate(DateTime.fromISO(dateMoment.toISOString()).toISODate());
  };

  return (
    <div className="cw-w-full cw-px-3 cw-mt-4 cw-mb-3 cw-flex cw-flex-row cw-items-center cw-justify-between cw-z-[51] cw-sticky">
      <div className="cw-flex cw-flex-row cw-items-center cw-gap-2 xs:cw-gap-3 cw-flex-wrap">
        <DatePicker
          id="day-date-picker"
          onChange={handleNavigateToDate}
          value={currentJSDate}
          variant="inline"
          autoOk
          disableToolbar
          format={compact ? "MMM YYYY" : "MMMM YYYY"}
          className={classNames(
            "cw-h-8 cw-text-13 cw-font-normal",
            "cw-px-2",
            "cw-font-body",
            "cw-rounded",
            "cw-transition",
            "cw-outline-1",
            "cw-text-default placeholder:cw-text-subtle",
            "cw-bg-default cw-border-default hover:cw-border-default-hover focus:cw-border-brand-interactable focus:hover:cw-border-brand-interactable-hover focus-visible:cw-outline-brand-interactable focus:cw-outline-brand-interactable focus:hover:cw-outline-brand-interactable-hover",
            "cw-cursor-pointer",
            "cw-justify-center",
          )}
          InputProps={{
            disableUnderline: true,
            classes: {
              root: "cw-cursor-pointer",
              input: "cw-cursor-pointer cw-font-bold cw-body-xl",
            },
            label: "Current Month",
            tabIndex: -1,
            role: "presentation",
          }}
          onClick={() => {
            // Click the keyboard icon to open the date picker.
            const changeDateIcon = document.querySelector(
              "[aria-label='change date']",
            ) as HTMLElement;
            if (changeDateIcon) {
              changeDateIcon.click?.();
            }
          }}
        />
        {!loading && errorMessage && <Notification sentiment="error">{errorMessage}</Notification>}
      </div>
      <div className="cw-flex cw-items-center cw-space-x-2">
        {!!calendarPeek && (
          <MultiCalendarSelect
            onMultiCalAdd={onMultiCalAdd}
            onMultiCalRemove={onMultiCalRemove}
            previewedCalendarId={previewedCalendarId}
            selectedCalendarIds={selectedCalendarIds}
          />
        )}
        <CalendarStepNavigation
          mode={mode}
          onPrevious={onShiftPrevious}
          onToday={onTodayClick}
          onNext={onShiftNext}
          highlightedButton={navButtonToHighlight}
          tooltipText={highlightedNavButtonTooltip}
        />
        <CalendarMenu
          calendarMode={mode}
          declinedEvents={declinedEventsEnabled}
          density={density}
          onCalendarDensityChange={onCalendarDensityChange}
          onCalendarModeChange={onCalendarModeChange}
          onShowDeclinedEventsChange={onShowDeclinedEventsChange}
          onShowHoldsChange={onShowHoldsChange}
          onShowWeekendsChange={onShowWeekendsChange}
          showHolds={showHolds}
          weekends={weekendsEnabled}
        />
        <CalendarMoreOptions changelogUrl={changelogURL} feedbackURL={feedbackURL} />
      </div>
    </div>
  );
};

export default PlannerToolbarRender;
